import React, { useState, useMemo } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { AttributeType, PlacedBlock, SocketConnection } from '@/app/types/cad';
import { useLanguage } from '@/app/contexts/LanguageContext';

export type FillMode = 'empty' | 'same' | 'numbering' | 'custom';

export interface AddAttributeConfig {
  name: string;
  type: AttributeType;
  targetObjects: {
    blocks: boolean;
    connections: boolean;
    specificBlockType?: string; // Name of specific block type
    allBlockTypes?: boolean; // All block types or specific one
  };
  fillMode: FillMode;
  fillValue?: string | number;
  startNumber?: number;
  prefix?: string;
  suffix?: string;
}

interface AddAttributeDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (config: AddAttributeConfig) => void;
  columnLabel?: string;
  blocks?: PlacedBlock[];
  connections?: SocketConnection[];
  initialName?: string; // For edit mode
  isEditMode?: boolean; // Flag to indicate edit mode
}

export function AddAttributeDialog({
  open,
  onClose,
  onConfirm,
  columnLabel,
  blocks = [],
  connections = [],
  initialName,
  isEditMode = false,
}: AddAttributeDialogProps) {
  const { t } = useLanguage();
  const [config, setConfig] = useState<AddAttributeConfig>({
    name: initialName || columnLabel || '',
    type: 'string',
    targetObjects: {
      blocks: true,
      connections: false,
      allBlockTypes: true,
    },
    fillMode: 'empty',
    fillValue: '',
    startNumber: 1,
    prefix: '',
    suffix: '',
  });

  // Collect unique block types
  const blockTypes = useMemo(() => {
    const types = new Map<string, number>();
    blocks.forEach(block => {
      const name = block.blockData.name || 'Unbenannt';
      types.set(name, (types.get(name) || 0) + 1);
    });
    return Array.from(types.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [blocks]);

  const handleConfirm = () => {
    console.log('📝 handleConfirm - Full config:', config);
    
    if (!config.name.trim()) {
      alert(t('dialogs.addAttribute.validationName'));
      return;
    }

    if (!config.targetObjects.blocks && !config.targetObjects.connections) {
      alert(t('dialogs.addAttribute.validationTarget'));
      return;
    }

    onConfirm(config);
    onClose();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
      }}
      onClick={onClose}
    >
      <div
        className="rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        style={{
          backgroundColor: 'var(--md-surface)',
          color: 'var(--md-on-surface)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: 'var(--md-outline)' }}
        >
          <h2 className="text-xl font-semibold">
            {isEditMode ? t('dialogs.addAttribute.titleEdit') : t('dialogs.addAttribute.titleNew')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded hover:bg-opacity-10"
            style={{
              color: 'var(--md-on-surface)',
            }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-6">
          {/* Attribut-Name */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {t('dialogs.addAttribute.nameLabel')}
            </label>
            <input
              type="text"
              value={config.name}
              onChange={(e) => setConfig({ ...config, name: e.target.value })}
              placeholder={t('dialogs.addAttribute.namePlaceholder')}
              className="w-full px-3 py-2 rounded border outline-none"
              style={{
                borderColor: 'var(--md-outline)',
                backgroundColor: 'var(--md-surface-container)',
                color: 'var(--md-on-surface)',
              }}
              autoFocus
            />
          </div>

          {/* Attribut-Typ */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {t('dialogs.addAttribute.typeLabel')}
            </label>
            <select
              value={config.type}
              onChange={(e) => setConfig({ ...config, type: e.target.value as AttributeType })}
              className="w-full px-3 py-2 rounded border outline-none"
              style={{
                borderColor: 'var(--md-outline)',
                backgroundColor: 'var(--md-surface-container)',
                color: 'var(--md-on-surface)',
              }}
            >
              <option value="string">{t('attributes.types.string')}</option>
              <option value="number">{t('attributes.types.number')}</option>
              <option value="integer">{t('attributes.types.integer')}</option>
              <option value="boolean">{t('attributes.types.boolean')}</option>
              <option value="dn">{t('attributes.types.dn')}</option>
              <option value="diameter-outer">da [mm] (Außendurchmesser Dämmung)</option>
              <option value="wall-thickness">{t('attributes.types.wallThickness')}</option>
              <option value="temperature">{t('attributes.types.temperature')}</option>
              <option value="pressure">{t('attributes.types.pressure')}</option>
              <option value="flow-rate">{t('attributes.types.flowRate')}</option>
              <option value="velocity">{t('attributes.types.velocity')}</option>
            </select>
          </div>

          {/* Ziel-Objekttypen */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {t('dialogs.addAttribute.targetLabel')}
            </label>
            <div className="space-y-3">
              {/* Bauteile */}
              <div className="border rounded p-3" style={{ borderColor: 'var(--md-outline)' }}>
                <label className="flex items-center space-x-2 cursor-pointer mb-2">
                  <input
                    type="checkbox"
                    checked={config.targetObjects.blocks}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        targetObjects: {
                          ...config.targetObjects,
                          blocks: e.target.checked,
                        },
                      })
                    }
                    className="w-4 h-4"
                  />
                  <span className="font-medium">{t('dialogs.addAttribute.targetBlocks')}</span>
                </label>

                {config.targetObjects.blocks && (
                  <div className="ml-6 space-y-2">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="blockSelection"
                        checked={config.targetObjects.allBlockTypes === true}
                        onChange={() =>
                          setConfig({
                            ...config,
                            targetObjects: {
                              ...config.targetObjects,
                              allBlockTypes: true,
                              specificBlockType: undefined,
                            },
                          })
                        }
                        className="w-4 h-4"
                      />
                      <span>{t('dialogs.addAttribute.targetAllBlocks').replace('{count}', blocks.length.toString())}</span>
                    </label>

                    <label className="flex items-start space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="blockSelection"
                        checked={config.targetObjects.allBlockTypes === false}
                        onChange={() =>
                          setConfig({
                            ...config,
                            targetObjects: {
                              ...config.targetObjects,
                              allBlockTypes: false,
                              specificBlockType: blockTypes[0]?.[0],
                            },
                          })
                        }
                        className="w-4 h-4 mt-1"
                      />
                      <div className="flex-1">
                        <div>{t('dialogs.addAttribute.targetSpecificBlock')}</div>
                        {config.targetObjects.allBlockTypes === false && (
                          <select
                            value={config.targetObjects.specificBlockType || ''}
                            onChange={(e) =>
                              setConfig({
                                ...config,
                                targetObjects: {
                                  ...config.targetObjects,
                                  specificBlockType: e.target.value,
                                },
                              })
                            }
                            className="w-full mt-2 px-3 py-2 rounded border outline-none text-sm"
                            style={{
                              borderColor: 'var(--md-outline)',
                              backgroundColor: 'var(--md-surface)',
                              color: 'var(--md-on-surface)',
                            }}
                          >
                            {blockTypes.map(([name, count]) => (
                              <option key={name} value={name}>
                                {name} ({count} Instanz{count !== 1 ? 'en' : ''})
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </label>
                  </div>
                )}
              </div>

              {/* Verbindungen */}
              <div className="border rounded p-3" style={{ borderColor: 'var(--md-outline)' }}>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.targetObjects.connections}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        targetObjects: {
                          ...config.targetObjects,
                          connections: e.target.checked,
                        },
                      })
                    }
                    className="w-4 h-4"
                  />
                  <span className="font-medium">
                    {t('dialogs.addAttribute.targetConnections').replace('{count}', connections.length.toString())}
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Wert-Füllmodus */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {t('dialogs.addAttribute.fillModeLabel')}
            </label>
            <div className="space-y-3">
              {/* Leer lassen */}
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="fillMode"
                  checked={config.fillMode === 'empty'}
                  onChange={() => setConfig({ ...config, fillMode: 'empty' })}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium">{t('dialogs.addAttribute.fillEmpty')}</div>
                  <div className="text-sm" style={{ color: 'var(--md-on-surface-variant)' }}>
                    {t('dialogs.addAttribute.fillEmptyDesc')}
                  </div>
                </div>
              </label>

              {/* Gleicher Wert */}
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="fillMode"
                  checked={config.fillMode === 'same'}
                  onChange={() => setConfig({ ...config, fillMode: 'same' })}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-medium">{t('dialogs.addAttribute.fillSame')}</div>
                  <div className="text-sm mb-2" style={{ color: 'var(--md-on-surface-variant)' }}>
                    {t('dialogs.addAttribute.fillSameDesc')}
                  </div>
                  {config.fillMode === 'same' && (
                    <input
                      type={config.type === 'number' || config.type === 'integer' ? 'number' : 'text'}
                      value={config.fillValue || ''}
                      onChange={(e) => {
                        const rawValue = e.target.value;
                        let parsedValue: string | number = rawValue;
                        
                        // Convert to number if type is number/integer
                        if (config.type === 'number' || config.type === 'integer') {
                          parsedValue = rawValue === '' ? '' : parseFloat(rawValue);
                        }
                        
                        console.log('📝 fillValue changed:', { rawValue, parsedValue, type: config.type });
                        setConfig({ ...config, fillValue: parsedValue });
                      }}
                      placeholder="Wert eingeben..."
                      className="w-full px-3 py-2 rounded border outline-none"
                      style={{
                        borderColor: 'var(--md-outline)',
                        backgroundColor: 'var(--md-surface-container)',
                        color: 'var(--md-on-surface)',
                      }}
                    />
                  )}
                </div>
              </label>

              {/* Fortlaufende Nummerierung */}
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="fillMode"
                  checked={config.fillMode === 'numbering'}
                  onChange={() => setConfig({ ...config, fillMode: 'numbering' })}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-medium">{t('dialogs.addAttribute.fillNumbering')}</div>
                  <div className="text-sm mb-2" style={{ color: 'var(--md-on-surface-variant)' }}>
                    {t('dialogs.addAttribute.fillNumberingDesc')}
                  </div>
                  {config.fillMode === 'numbering' && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs mb-1">{t('dialogs.addAttribute.startValue')}</label>
                        <input
                          type="number"
                          value={config.startNumber || 1}
                          onChange={(e) =>
                            setConfig({ ...config, startNumber: parseInt(e.target.value) || 1 })
                          }
                          className="w-full px-3 py-2 rounded border outline-none"
                          style={{
                            borderColor: 'var(--md-outline)',
                            backgroundColor: 'var(--md-surface-container)',
                            color: 'var(--md-on-surface)',
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs mb-1">{t('dialogs.addAttribute.prefix')}</label>
                        <input
                          type="text"
                          value={config.prefix || ''}
                          onChange={(e) =>
                            setConfig({ ...config, prefix: e.target.value })
                          }
                          placeholder="z.B. ID-"
                          className="w-full px-3 py-2 rounded border outline-none"
                          style={{
                            borderColor: 'var(--md-outline)',
                            backgroundColor: 'var(--md-surface-container)',
                            color: 'var(--md-on-surface)',
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs mb-1">{t('dialogs.addAttribute.suffix')}</label>
                        <input
                          type="text"
                          value={config.suffix || ''}
                          onChange={(e) =>
                            setConfig({ ...config, suffix: e.target.value })
                          }
                          placeholder="z.B. -A"
                          className="w-full px-3 py-2 rounded border outline-none"
                          style={{
                            borderColor: 'var(--md-outline)',
                            backgroundColor: 'var(--md-surface-container)',
                            color: 'var(--md-on-surface)',
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </label>

              {/* Benutzerdefiniert */}
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="fillMode"
                  checked={config.fillMode === 'custom'}
                  onChange={() => setConfig({ ...config, fillMode: 'custom' })}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium">{t('dialogs.addAttribute.fillCustom')}</div>
                  <div className="text-sm" style={{ color: 'var(--md-on-surface-variant)' }}>
                    {t('dialogs.addAttribute.fillCustomDesc')}
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Preview */}
          {config.fillMode === 'numbering' && (
            <div
              className="p-3 rounded"
              style={{
                backgroundColor: 'var(--md-surface-container-high)',
              }}
            >
              <div className="text-xs font-medium mb-1">{t('dialogs.addAttribute.preview')}</div>
              <div className="text-sm" style={{ color: 'var(--md-on-surface-variant)' }}>
                {config.prefix}{config.startNumber || 1}{config.suffix},{' '}
                {config.prefix}{(config.startNumber || 1) + 1}{config.suffix},{' '}
                {config.prefix}{(config.startNumber || 1) + 2}{config.suffix}, ...
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end space-x-3 px-6 py-4 border-t"
          style={{ borderColor: 'var(--md-outline)' }}
        >
          <Button
            onClick={onClose}
            variant="outline"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            style={{
              backgroundColor: 'var(--md-primary)',
              color: 'var(--md-on-primary)',
            }}
          >
            {isEditMode ? t('dialogs.addAttribute.saveBtn') : t('dialogs.addAttribute.createBtn')}
          </Button>
        </div>
      </div>
    </div>
  );
}
